import { defineComponent, h } from "vue";
import { usePage } from "@inertiajs/vue3";
import definedComponents from "./components";
import { replaceS3WithCloudfront } from "./helpers";
import { useSetting } from "@/Hook/useSetting";

// Node type constants — avoids referencing the `Node` DOM global, which is
// absent during SSR.
const ELEMENT_NODE = 1;
const TEXT_NODE = 3;

// Form controls that need an accessible name (axe/Lighthouse "Form elements
// must have labels"). CMS authors routinely drop labels — e.g. the Salesforce
// web-to-lead fields — so we backfill a name the same way we backfill img alt
// text and iframe titles below.
const FORM_CONTROL_TAGS = new Set(["input", "select", "textarea"]);

// Input types that render no user-editable field, so they need no label.
const UNLABELED_INPUT_TYPES = new Set([
    "hidden",
    "submit",
    "button",
    "reset",
    "image",
]);

// Human-readable fallback names by input type, used only when the author gave
// the control no placeholder to borrow from.
const INPUT_TYPE_LABELS = {
    date: "Date",
    "datetime-local": "Date and time",
    time: "Time",
    month: "Month",
    week: "Week",
    email: "Email address",
    tel: "Phone number",
    number: "Number",
    search: "Search",
    url: "Web address",
    password: "Password",
    color: "Color",
    file: "File",
    text: "Text",
};

// Pick a fallback accessible name for a labelless form control. Prefers the
// author's own placeholder text, then a type-specific default; returns "" when
// nothing sensible is available (caller falls back to the site name).
const controlFallbackName = function (tagName, props) {
    const placeholder =
        typeof props.placeholder === "string" ? props.placeholder.trim() : "";
    if (placeholder) return placeholder;

    if (tagName === "input") {
        const type =
            typeof props.type === "string" ? props.type.toLowerCase() : "text";
        return INPUT_TYPE_LABELS[type] || "Text";
    }
    if (tagName === "select") return "Select an option";
    if (tagName === "textarea") return "Message";

    return "";
};

// Pluggable server-side HTML parser. The browser has a real DOM; Node does not,
// so `ssr.ts` injects a parser (linkedom) via setServerHtmlParser() so CMS
// content can be turned into vnodes on the server too.
let serverHtmlParser = null;

export function setServerHtmlParser(parser) {
    serverHtmlParser = parser;
}

// Parse an HTML string into a flat list of top-level child nodes: the browser
// DOM on the client, the injected parser on the server. The returned nodes must
// expose the DOM-ish shape transformNode() relies on (nodeType, tagName,
// attributes, childNodes, nodeValue) — linkedom matches this exactly.
const parseToChildNodes = function (html) {
    if (typeof document !== "undefined") {
        const container = document.createElement("div");
        container.innerHTML = html;
        return Array.from(container.childNodes);
    }

    if (serverHtmlParser) {
        return serverHtmlParser(html);
    }

    // No parser registered (e.g. SSR misconfigured) — render nothing rather than
    // throw; the client fills the content in on hydration.
    return [];
};

const convertShortcodes = function (_, tag, attrs) {
    try {
        const formattedAttrs = attrs
            .trim()
            .replace(/(\w+)=([\w\-:/\.]+)/g, '$1="$2"'); // Ensure attributes are quoted
        // Explicit close tag, not self-closing: custom elements aren't void in
        // HTML, so `<tag/>` parses as an unclosed open tag and swallows every
        // following sibling — which breaks having more than one shortcode (e.g.
        // two `[posts]`) in the same content.
        return `<${tag} ${formattedAttrs}></${tag}>`;
    } catch (err) {
        console.warn("Error Converting Shortcode: " + tag, err);
        return "";
    }
};

const parseString = function (str) {
    try {
        const parsed = JSON.parse(str);
        return parsed;
    } catch (error) {}

    return str;
};

// Decode the base64-encoded JSON payload the server attaches to data
// shortcodes (see App\Support\ShortcodeResolver). Works in Node (Buffer) and
// the browser (atob), so seeded shortcode data is available in component setup
// during both SSR and client hydration — no onMounted fetch, no blank flash.
const decodeSsrData = function (encoded) {
    try {
        const json =
            typeof Buffer !== "undefined"
                ? Buffer.from(encoded, "base64").toString("utf-8")
                : decodeURIComponent(escape(atob(encoded)));
        return JSON.parse(json);
    } catch (err) {
        console.warn("Failed to decode shortcode ssrdata", err);
        return undefined;
    }
};

const transformNode = function (node, siteName) {
    if (node.nodeType === ELEMENT_NODE) {
        const tagName = node.tagName.toLowerCase();

        // Extract attributes as a props object
        const props = Array.from(node.attributes).reduce((acc, attr) => {
            const match = attr.name.match(/^([a-zA-Z0-9_]+)-([a-zA-Z0-9_]+)$/);

            if (match) {
                const [, name, sub_attr] = match;

                if (!acc[name]) acc[name] = {};
                acc[name][sub_attr] = parseString(attr.value);
            }

            acc[attr.name] = parseString(attr.value);

            return acc;
        }, {});

        // Ensure CMS-authored images always carry alt text: when the author
        // didn't set an `alt` attribute, fall back to the site name (a11y/SEO).
        if (tagName === "img" && siteName && !("alt" in props)) {
            props.alt = siteName;
        }

        // Ensure embedded frames always have a title (required for a11y — an
        // untitled iframe/frame has no accessible name). Fall back to the site
        // name when the author didn't set one.
        if (
            (tagName === "iframe" || tagName === "frame") &&
            siteName &&
            !("title" in props)
        ) {
            props.title = siteName;
        }

        // Ensure form controls always expose an accessible name. We add `title`
        // rather than `aria-label` on purpose: `title` ranks below a native
        // `<label>` in the accessible-name computation, so a control that
        // already has a proper (possibly external `for`-associated) label keeps
        // it, while a labelless control — invisible to this node-local
        // transform otherwise — still gets a name and passes the audit.
        if (FORM_CONTROL_TAGS.has(tagName)) {
            const inputType =
                tagName === "input" && typeof props.type === "string"
                    ? props.type.toLowerCase()
                    : "text";
            const needsName =
                tagName !== "input" || !UNLABELED_INPUT_TYPES.has(inputType);
            const hasName =
                "aria-label" in props ||
                "aria-labelledby" in props ||
                ("title" in props && String(props.title).trim() !== "");

            if (needsName && !hasName) {
                props.title = controlFallbackName(tagName, props) || siteName;
            }
        }

        // Replace custom components with Vue component definitions
        if (definedComponents[tagName]) {
            // Server-resolved shortcode payload: decode it into a real object
            // and hand it to the component as `ssrData` so it renders during
            // SSR instead of fetching on mount.
            if (typeof props.ssrdata === "string") {
                props.ssrData = decodeSsrData(props.ssrdata);
                delete props.ssrdata;
            }
            return h(definedComponents[tagName], props, () =>
                Array.from(node.childNodes).map((child) =>
                    transformNode(child, siteName)
                )
            );
        }

        // For regular elements, keep the tag and process children
        return h(
            tagName,
            props,
            Array.from(node.childNodes).map((child) =>
                transformNode(child, siteName)
            )
        );
    }

    // Handle text nodes
    if (node.nodeType === TEXT_NODE) {
        return node.nodeValue;
    }
};

const parseHtmlWithComponents = function (html, siteName) {
    const regex = /(?<!["'\w-])\[(\w+)((?:\s+[\w-]+=(?:\S+|"[^"]*"|'[^']*'))+)?\](?!["'\w-])/gu;


    const converted = html.replace(regex, convertShortcodes);

    // Transform the top-level nodes. Parsing works on both client (browser DOM)
    // and server (injected linkedom parser), so CMS content renders during SSR.
    return parseToChildNodes(converted).map((node) =>
        transformNode(node, siteName)
    );
};

export default function defineContent(html) {
    return defineComponent({
        render() {
            const page = usePage();
            const siteName = useSetting().getSetting("site_name", "");
            const transformedHtml = replaceS3WithCloudfront(
                html,
                page.props.__awsBucket,
                page.props.__awsUrl
            );
            return h("div", {}, parseHtmlWithComponents(transformedHtml, siteName));
        },
    });
}

export function defineElement(tag, content) {
    return defineComponent({
        render() {
            return h(tag, {}, content);
        },
    });
}
